/**
 * SmbSyncWizard - Orchestrates the 5-step SMB Vendor Cost Import workflow
 *
 * Manages wizard state and navigation between steps
 */

import React, { useState } from 'react'
import { Steps, App } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { CatalogUploadStep } from './CatalogUploadStep'
import { VendorUploadStep } from './VendorUploadStep'
import { TabSelectionStep } from './TabSelectionStep'
import { FieldMappingStep } from './FieldMappingStep'
import { PricingRuleStep } from './PricingRuleStep'
import { ResultsStep } from './ResultsStep'
import type { WizardState, WizardStep } from './types'

const { Step } = Steps

const initialState: WizardState = {
  currentStep: 'catalog',
  jobId: '',
  platform: 'square',
  catalogFile: null,
  vendorFile: null,
  catalogColumns: [],
  vendorTabs: [],
  selectedTabs: [],
  vendorSampleData: null,
  catalogMappings: [],
  vendorMappings: [],
  tableFieldMappings: [],
  pricingBasis: 'cost',
  priceCalculationMethod: 'formula',
  costMultiplier: 1.2,
  costAdder: 0,
  roundingRule: 'nearest_penny',
  processResult: null,
}

/**
 * 5-step wizard orchestrator
 */
export const SmbSyncWizard: React.FC = () => {
  const { modal } = App.useApp()
  const [state, setState] = useState<WizardState>(initialState)
  const [resetKey, setResetKey] = useState(0)

  const handleNext = (updates: Partial<WizardState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates }

      // Auto-advance to next step if not explicitly set
      // Note: VendorUploadStep explicitly sets currentStep based on file type
      if (!updates.currentStep) {
        if (prev.currentStep === 'catalog') {
          newState.currentStep = 'vendor'
        } else if (prev.currentStep === 'vendor') {
          // VendorUploadStep sets this explicitly (tabs or mapping)
          newState.currentStep = 'mapping'
        } else if (prev.currentStep === 'tabs') {
          newState.currentStep = 'mapping'
        } else if (prev.currentStep === 'mapping') {
          newState.currentStep = 'pricing'
        } else if (prev.currentStep === 'pricing') {
          newState.currentStep = 'results'
        }
      }

      return newState
    })
  }

  const handleBack = () => {
    setState((prev) => ({
      ...prev,
      currentStep:
        prev.currentStep === 'vendor'
          ? 'catalog'
          : prev.currentStep === 'tabs'
          ? 'vendor'
          : prev.currentStep === 'mapping'
          ? (prev.vendorTabs.length > 0 ? 'tabs' : 'vendor') // Go back to tabs if Excel, vendor if CSV
          : prev.currentStep === 'pricing'
          ? 'mapping'
          : prev.currentStep === 'results'
          ? 'pricing'
          : prev.currentStep,
    }))
  }

  const handleCancel = () => {
    modal.confirm({
      title: 'Cancel vendor import?',
      icon: <ExclamationCircleOutlined />,
      content: "Any uploaded files and field mappings will be lost. You can always start over with the same files later.",
      okText: 'Yes, Cancel Import',
      cancelText: 'Keep Working',
      okType: 'danger',
      onOk: () => {
        setState(initialState)
        setResetKey((prev) => prev + 1) // Force remount of step components
      },
    })
  }

  const stepMap: Record<WizardStep, number> = {
    catalog: 0,
    vendor: 1,
    tabs: 1, // Tabs is between vendor and mapping, use same index as vendor
    mapping: 2,
    pricing: 3,
    results: 4,
  }

  return (
    <div>
      {/* Progress Steps */}
      <Steps current={stepMap[state.currentStep]} style={{ marginBottom: '24px' }}>
        <Step title="Upload Catalog" description="Square or QuickBooks" />
        <Step title="Upload Vendor" description="Cost file" />
        <Step title="Map Fields" description="Join keys" />
        <Step title="Configure Pricing" description="Cost & price rules" />
        <Step title="Results" description="Download files" />
      </Steps>

      {/* Current Step Component - key forces remount on Cancel */}
      {state.currentStep === 'catalog' && (
        <CatalogUploadStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
      {state.currentStep === 'vendor' && (
        <VendorUploadStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
      {state.currentStep === 'tabs' && (
        <TabSelectionStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
      {state.currentStep === 'mapping' && (
        <FieldMappingStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
      {state.currentStep === 'pricing' && (
        <PricingRuleStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
      {state.currentStep === 'results' && (
        <ResultsStep key={resetKey} state={state} onNext={handleNext} onBack={handleBack} onCancel={handleCancel} />
      )}
    </div>
  )
}
